import { isPlatformBrowser } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DOCUMENT,
    ElementRef,
    HostBinding,
    inject,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
    ViewChild
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, fromEvent, Observable, Subscription } from 'rxjs';
import { bufferTime, filter, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LayoutHeaderComponentImports } from '../..';
import { ActiveService } from '../../providers/active/active.service';
import { StateService } from '../../providers/state/state.service';
import { WishlistService } from '../../providers/wishlist/wishlist.service';

type HeaderBranding =
    | { type: 'image'; src: string; alt: string; class: string }
    | { type: 'text'; text: string; class: string };

@Component({
    standalone: true,
    selector: 'vsf-layout-header',
    imports: LayoutHeaderComponentImports,
    templateUrl: './layout-header.component.html',
    styleUrls: ['./layout-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutHeaderComponent implements AfterViewInit, OnDestroy, OnInit {
    private platformId = inject(PLATFORM_ID);
    private stateService = inject(StateService);
    private activeService = inject(ActiveService);
    private translocoService = inject(TranslocoService);
    private wishlistService = inject(WishlistService);
    private document = inject(DOCUMENT);

    readonly headerBranding = environment.headerBranding as HeaderBranding;

    @HostBinding('class.floating')
    floating = false;

    @HostBinding('style.height.px')
    headerHeight: number | null;

    @ViewChild('floatingContainer', { static: false })
    private floatingContainer: ElementRef<HTMLDivElement>;

    cartItemCount$: Observable<number>;
    wishlistItemCount$: Observable<number>;
    selectedLanguage = 'de';
    languages$: Observable<{ code: string; name: string }[]> | undefined;
    readonly LANG_STORAGE_KEY = 'lang';
    readonly DEFAULT_LANG = 'de';
    readonly SUPPORTED_LANGS = ['en', 'de'];

    private subscription: Subscription;

    ngOnInit() {
        this.cartItemCount$ = this.activeService.activeOrder$.pipe(
            map(order => {
                if (!order || !order.lines || order.lines.length === 0) {
                    return 0;
                }
                return order.totalQuantity;
            })
        );
        this.wishlistItemCount$ = combineLatest([
            this.wishlistService.wishlistItems,
            this.stateService.select(state => state.signedIn)
        ]).pipe(
            map(([items, signedIn]) => signedIn ? items.length : 0)
        );

        if (isPlatformBrowser(this.platformId)) {
            let lang = localStorage.getItem(this.LANG_STORAGE_KEY);

            if (!lang || !this.SUPPORTED_LANGS.includes(lang)) {
                lang = this.DEFAULT_LANG;
                localStorage.setItem(this.LANG_STORAGE_KEY, lang);
        }
            this.translocoService.setActiveLang(lang);
            this.selectedLanguage = lang;
        }
    }

    ngAfterViewInit() {
        if (isPlatformBrowser(this.platformId)) {
            const win = this.document.defaultView;
            if (win) {
                this.setUpScrollHandler(win);
            }
        }
    }

    private setUpScrollHandler(_window: Window) {
        this.subscription = fromEvent(_window, 'scroll', { passive: true })
            .pipe(
                map(() => _window.scrollY),
                bufferTime(250),
                filter((val) => 1 < val.length),
                map((val) => val[val.length - 1] - val[0])
            )
            .subscribe((val) => {
                if (_window.scrollY === 0) {
                    this.setFloating(false);
                } else if (0 < val) {
                    this.setFloating(false);
                } else if (val < -50 && 300 < _window.scrollY) {
                    this.setFloating(true);
                }
            });
    }

    private setFloating(isFloating: boolean) {
        this.floating = isFloating;
        this.headerHeight = this.floatingContainer.nativeElement.offsetHeight;
    }

    openCartDrawer() {
        this.stateService.setState('cartDrawerOpen', true);
    }

    changeLang(lang: string) {
        if (!this.SUPPORTED_LANGS.includes(lang)) {
            return;
        }

        this.translocoService.setActiveLang(lang);
        localStorage.setItem(this.LANG_STORAGE_KEY, lang);
        this.selectedLanguage = lang;
    }

    // Map language codes to available flag codes used by the PrimeNG demo CSS
    flagCode(code: string): string {
        if (!code) return '';
        // Use GB flag for English, keep others as-is
        return code === 'en' ? 'gb' : code.toLowerCase();
    }

    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
}
